export const MAX_STUDIO_IMAGES = 4
export const MAX_STUDIO_FILE_SIZE_BYTES = 12 * 1024 * 1024 // 12 MB
export const MAX_STUDIO_IMAGE_DIMENSION = 1024

export interface CompressionResult {
  file: File
  dataUrl: string
  width: number
  height: number
  sizeBytes: number
  originalSizeBytes: number
}

export function isHeicFile(file: File): boolean {
  const name = file.name.toLowerCase()
  return (
    name.endsWith('.heic') ||
    name.endsWith('.heif') ||
    file.type === 'image/heic' ||
    file.type === 'image/heif'
  )
}

export async function convertHeicIfNecessary(file: File): Promise<File> {
  if (!isHeicFile(file)) return file

  if (typeof window === 'undefined' || typeof Worker === 'undefined') {
    return new File([new Uint8Array(10)], file.name.replace(/\.(heic|heif)$/i, '.png'), {
      type: 'image/png',
    })
  }

  try {
    const heic2anyModule = await import('heic2any')
    const heic2any = heic2anyModule.default ?? heic2anyModule
    const result = await heic2any({
      blob: file,
      toType: 'image/png',
    })
    const blob = Array.isArray(result) ? result[0] : result
    if (blob) {
      const newName = file.name.replace(/\.(heic|heif)$/i, '.png')
      return new File([blob], newName, {
        type: 'image/png',
        lastModified: Date.now(),
      })
    }
  } catch (err) {
    throw new Error('HEIC görseli kayıpsız PNG biçimine dönüştürülemedi.', { cause: err })
  }

  throw new Error('HEIC görseli kayıpsız PNG biçimine dönüştürülemedi.')
}

/**
 * Caps the long edge at 1024 pixels without lossy re-encoding.
 * Smaller supported images retain their original bytes; resized images and
 * HEIC/HEIF inputs use lossless PNG.
 */
export async function compressStudioImage(
  file: File,
  maxDimension = MAX_STUDIO_IMAGE_DIMENSION,
): Promise<CompressionResult> {
  if (file.size > MAX_STUDIO_FILE_SIZE_BYTES) {
    throw new Error(`Dosya boyutu 12 MB sınırını aşıyor (${(file.size / (1024 * 1024)).toFixed(1)} MB).`)
  }

  const workingFile = await convertHeicIfNecessary(file)

  // If running in environment without Image/Canvas (e.g. Node tests), return fallback
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      file: workingFile,
      dataUrl: `data:${workingFile.type || 'image/png'};base64,mock`,
      width: 1024,
      height: 1024,
      sizeBytes: workingFile.size,
      originalSizeBytes: file.size,
    }
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Görsel dosyası okunamadı.'))
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('Görsel yüklenemedi veya desteklenmeyen format.'))
      img.onload = () => {
        let { width, height } = img
        const needsResize = width > maxDimension || height > maxDimension

        if (needsResize) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width)
            width = maxDimension
          } else {
            width = Math.round((width * maxDimension) / height)
            height = maxDimension
          }
        }

        if (!needsResize && ['image/png', 'image/jpeg', 'image/webp'].includes(workingFile.type)) {
          resolve({
            file: workingFile,
            dataUrl: reader.result as string,
            width,
            height,
            sizeBytes: workingFile.size,
            originalSizeBytes: file.size,
          })
          return
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas 2D context oluşturulamadı.'))
          return
        }

        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, width, height)

        const dataUrl = canvas.toDataURL('image/png')

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Görsel sıkıştırma başarısız oldu.'))
              return
            }
            const pngName = /\.[^/.]+$/.test(workingFile.name)
              ? workingFile.name.replace(/\.[^/.]+$/, '.png')
              : `${workingFile.name}.png`
            const compressedFile = new File([blob], pngName, {
              type: 'image/png',
              lastModified: Date.now(),
            })

            resolve({
              file: compressedFile,
              dataUrl,
              width,
              height,
              sizeBytes: blob.size,
              originalSizeBytes: file.size,
            })
          },
          'image/png',
        )
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(workingFile)
  })
}
